/**
 * Autenticação por ABA (sessionStorage) + espelho no localStorage.
 *
 * Permite master / representante / usuário de workspace em abas diferentes
 * no mesmo domínio — cada aba lê o próprio sessionStorage.
 *
 * - Leitura: sessionStorage → se vazio, migra uma cópia do localStorage (Ctrl+T / aba órfã)
 * - Escrita (login): sessionStorage desta aba + espelho no localStorage (herança de abas novas)
 * - Logout: limpa session desta aba; remove espelho local só se for o mesmo token
 * - Nova aba do mesmo usuário (openWorkspace): clonar via copyTabAuthToWindow / openWindowWithTabAuth
 */

const AUTH_KEYS = ["bearerToken", "userData", "tokenExpiration", "lastActivity", "authWorkspaceSlug"];

function isLikelyJwt(token) {
  if (token == null || typeof token !== "string") return false;
  const t = token.trim();
  if (!t || t === "undefined" || t === "null") return false;
  const parts = t.split(".");
  return parts.length === 3 && parts.every((p) => p.length > 0);
}

function ss() {
  return typeof sessionStorage !== "undefined" ? sessionStorage : null;
}

function ls() {
  return typeof localStorage !== "undefined" ? localStorage : null;
}

function safeGet(store, key) {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(store, key, value) {
  try {
    if (!store) return;
    if (value == null || value === "") store.removeItem(key);
    else store.setItem(key, String(value));
  } catch {
    /* ignore */
  }
}

function safeRemove(store, key) {
  try {
    store?.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Migra auth legado/espelho do localStorage → sessionStorage desta aba. */
function migrateLegacyAuthToTab() {
  const s = ss();
  const l = ls();
  if (!s || !l) return;
  try {
    if (safeGet(s, "bearerToken")) return;
    const token = safeGet(l, "bearerToken");
    if (!isLikelyJwt(token)) return;
    for (const key of AUTH_KEYS) {
      const v = safeGet(l, key);
      if (v != null) safeSet(s, key, v);
    }
  } catch {
    /* ignore */
  }
}

/** Espelho local para abas novas (Ctrl+T). Não afeta sessionStorage de outras abas. */
function mirrorAuthKeyToLocal(key, value) {
  safeSet(ls(), key, value);
}

/** Remove espelho local apenas se for o token desta aba (não derruba outras sessões). */
function clearLocalAuthMirrorIfSameToken(tabToken) {
  const l = ls();
  if (!l) return;
  const localToken = safeGet(l, "bearerToken");
  if (tabToken && localToken && String(localToken).trim() === String(tabToken).trim()) {
    for (const key of AUTH_KEYS) safeRemove(l, key);
    return;
  }
  if (!tabToken && !isLikelyJwt(localToken)) {
    for (const key of AUTH_KEYS) safeRemove(l, key);
  }
}

export function readTabBearerToken() {
  migrateLegacyAuthToTab();
  const raw = safeGet(ss(), "bearerToken");
  if (!isLikelyJwt(raw)) {
    if (raw) safeRemove(ss(), "bearerToken");
    return null;
  }
  return String(raw).trim();
}

export function writeTabBearerToken(token) {
  if (!isLikelyJwt(token)) {
    const prev = safeGet(ss(), "bearerToken");
    safeRemove(ss(), "bearerToken");
    clearLocalAuthMirrorIfSameToken(prev);
    return null;
  }
  const normalized = String(token).trim();
  safeSet(ss(), "bearerToken", normalized);
  mirrorAuthKeyToLocal("bearerToken", normalized);
  return normalized;
}

export function clearTabBearerToken() {
  const prev = safeGet(ss(), "bearerToken");
  safeRemove(ss(), "bearerToken");
  clearLocalAuthMirrorIfSameToken(prev);
}

export function readTabUserData() {
  migrateLegacyAuthToTab();
  try {
    const raw = safeGet(ss(), "userData");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function readTabUserDataRaw() {
  migrateLegacyAuthToTab();
  return safeGet(ss(), "userData");
}

export function writeTabUserData(user) {
  if (user == null) {
    safeRemove(ss(), "userData");
    // não remove espelho local sozinho — logout usa clearTabAuthStorage
    return;
  }
  try {
    const raw = JSON.stringify(user);
    safeSet(ss(), "userData", raw);
    mirrorAuthKeyToLocal("userData", raw);
  } catch {
    /* ignore */
  }
}

export function readTabTokenExpiration() {
  migrateLegacyAuthToTab();
  return safeGet(ss(), "tokenExpiration");
}

export function writeTabTokenExpiration(value) {
  safeSet(ss(), "tokenExpiration", value);
  mirrorAuthKeyToLocal("tokenExpiration", value);
}

export function readTabLastActivity() {
  migrateLegacyAuthToTab();
  const v = safeGet(ss(), "lastActivity");
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function writeTabLastActivity(value) {
  safeSet(ss(), "lastActivity", value);
  mirrorAuthKeyToLocal("lastActivity", value);
}

/** Slug do workspace amarrado ao Bearer desta aba (scope workspace). */
export function readTabAuthWorkspaceSlug() {
  migrateLegacyAuthToTab();
  const raw = safeGet(ss(), "authWorkspaceSlug");
  const s = raw != null ? String(raw).trim().toLowerCase() : "";
  return s || null;
}

export function writeTabAuthWorkspaceSlug(slug) {
  const s = slug != null ? String(slug).trim().toLowerCase() : "";
  if (!s) {
    safeRemove(ss(), "authWorkspaceSlug");
    safeRemove(ls(), "authWorkspaceSlug");
    return null;
  }
  safeSet(ss(), "authWorkspaceSlug", s);
  mirrorAuthKeyToLocal("authWorkspaceSlug", s);
  return s;
}

export function clearTabAuthWorkspaceSlug() {
  safeRemove(ss(), "authWorkspaceSlug");
}

/**
 * Limpa auth desta aba. Remove espelho localStorage só se for o mesmo JWT —
 * assim logout em uma aba não invalida master/partner/workspace nas outras.
 */
export function clearTabAuthStorage() {
  const s = ss();
  const prevToken = safeGet(s, "bearerToken");
  for (const key of AUTH_KEYS) safeRemove(s, key);
  clearLocalAuthMirrorIfSameToken(prevToken);
}

/**
 * Copia a sessão desta aba para uma janela recém-aberta (mesmo origin),
 * antes de navegar — necessário com auth em sessionStorage.
 */
export function copyTabAuthToWindow(win) {
  if (!win) return false;
  try {
    migrateLegacyAuthToTab();
    const s = ss();
    if (!s) return false;
    for (const key of AUTH_KEYS) {
      const v = safeGet(s, key);
      if (v != null) win.sessionStorage.setItem(key, v);
    }
    return Boolean(safeGet(s, "bearerToken"));
  } catch {
    return false;
  }
}

/**
 * Abre janela same-origin clonando auth (sessionStorage) desta aba.
 * Usa about:blank → copy → assign para a URL alvo.
 */
export function openWindowWithTabAuth(url, windowName, features) {
  if (typeof window === "undefined" || !url) return null;
  const win = features
    ? window.open("about:blank", windowName || "_blank", features)
    : window.open("about:blank", windowName || "_blank");
  if (!win) return null;
  try {
    copyTabAuthToWindow(win);
  } catch {
    /* ignore */
  }
  try {
    win.location.assign(url);
  } catch {
    try {
      win.location.href = url;
    } catch {
      /* ignore */
    }
  }
  try {
    win.focus();
  } catch {
    /* ignore */
  }
  return win;
}
